/**
 * Additional-work offers on a work order already in progress (#3203 / epic #3195).
 *
 * Types live here (not in shared packages). The deliver contract is:
 * POST /work-orders/:id/offers/:offerId/deliver
 *   { channel: 'email' | 'whatsapp' | 'sms', recipient?, message?, expiresAt? }
 *   → { offer, delivered, reason?: 'preapproved', send? }
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from './queryKeys';

export type WorkOrderOfferStatus =
  | 'draft'
  | 'sent'
  | 'partially_decided'
  | 'approved'
  | 'declined'
  | 'expired'
  | 'cancelled'
  | 'superseded';

export type WorkOrderOfferLineDecision = 'pending' | 'approved' | 'declined';

export type OfferDecisionChannel = 'phone' | 'in_person' | 'email' | 'sms' | 'whatsapp' | 'other';

export const OFFER_DELIVER_CHANNELS = ['email', 'whatsapp', 'sms'] as const;
export type OfferDeliverChannel = (typeof OFFER_DELIVER_CHANNELS)[number];

export interface WorkOrderOfferLine {
  id: string;
  workOrderItemId: string;
  description: string;
  quantity: string | number;
  unitGrossCents: number | null;
  totalGrossCents: number | null;
  vatPercent: string | number | null;
  decision: WorkOrderOfferLineDecision;
  decidedAt: string | null;
  sortOrder: number;
}

export interface WorkOrderOfferLatestSend {
  sentVia: string;
  recipient: string;
  sentAt: string;
  respondedAt: string | null;
  response: string | null;
}

export interface WorkOrderOfferEvent {
  type: string;
  channel: string | null;
  decision: string | null;
  actorName: string | null;
  recordedBy: string | null;
  occurredAt: string | null;
  evidence?: unknown;
}

export interface WorkOrderOffer {
  id: string;
  bookingId: string;
  kind?: string;
  sequenceNo: number;
  status: WorkOrderOfferStatus;
  title: string | null;
  customerDescription: string | null;
  proposedTotalGrossCents: number | null;
  approvedTotalGrossCents: number | null;
  snapshotHash: string | null;
  sentAt: string | null;
  sentBy: string | null;
  expiresAt: string | null;
  decidedAt: string | null;
  source: string;
  version: number;
  createdAt: string | null;
  updatedAt: string | null;
  lines: WorkOrderOfferLine[];
  events: WorkOrderOfferEvent[];
  latestSend?: WorkOrderOfferLatestSend;
}

export interface WorkOrderOffersResponse {
  offers: WorkOrderOffer[];
}

export interface CreateOfferInput {
  itemIds: string[];
  title?: string;
  customerDescription?: string;
}

export interface DeliverOfferBody {
  channel: OfferDeliverChannel;
  recipient?: string;
  message?: string;
  expiresAt?: string;
}

export interface OfferDeliverSend {
  id: string;
  sentVia: string;
  recipient: string;
  sentAt: string;
  tokenExpiresAt: string;
}

export interface DeliverOfferResponse {
  offer: WorkOrderOffer;
  delivered: boolean;
  reason?: 'preapproved';
  send?: OfferDeliverSend;
}

export interface OfferLineDecisionInput {
  lineId: string;
  decision: 'approved' | 'declined';
}

export interface OfferCustomerApproval {
  statement: string;
  customerName: string;
  channel: OfferDecisionChannel;
  receivedAt: string;
  reference?: string;
}

export interface RecordOfferDecisionInput {
  decisions: 'all_approved' | 'all_declined' | OfferLineDecisionInput[];
  channel: OfferDecisionChannel;
  customerApproval: OfferCustomerApproval;
  expectedSnapshotHash: string;
  idempotencyKey: string;
}

export const PENDING_OFFER_STATUSES: readonly WorkOrderOfferStatus[] = ['sent', 'partially_decided'];
export const NON_TERMINAL_OFFER_STATUSES: readonly WorkOrderOfferStatus[] = [
  'draft',
  'sent',
  'partially_decided',
];

export function isPendingOfferStatus(status: string): boolean {
  return status === 'sent' || status === 'partially_decided';
}

export function isNonTerminalOfferStatus(status: string): boolean {
  return status === 'draft' || status === 'sent' || status === 'partially_decided';
}

export function workOrderOffersQueryKey(workOrderId: string) {
  return [...queryKeys.workOrders.detail(workOrderId), 'offers'] as const;
}

function invalidateOfferViews(
  queryClient: ReturnType<typeof useQueryClient>,
  workOrderId: string,
): void {
  void queryClient.invalidateQueries({ queryKey: workOrderOffersQueryKey(workOrderId) });
  void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.detail(workOrderId) });
  void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.history(workOrderId) });
  void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.lists() });
  void queryClient.invalidateQueries({ queryKey: queryKeys.workOrders.stats() });
  void queryClient.invalidateQueries({ queryKey: queryKeys.bookings.detail(workOrderId) });
  void queryClient.invalidateQueries({ queryKey: queryKeys.invoicePreview(workOrderId) });
  void queryClient.invalidateQueries({ queryKey: ['workOrderParts', workOrderId] });
}

export function useWorkOrderOffers(workOrderId: string | undefined) {
  return useQuery({
    queryKey: workOrderOffersQueryKey(workOrderId ?? ''),
    queryFn: () => api.get<WorkOrderOffersResponse>(`/work-orders/${workOrderId}/offers`),
    enabled: !!workOrderId,
    staleTime: 30 * 1000,
  });
}

export function useCreateOffer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ workOrderId, data }: { workOrderId: string; data: CreateOfferInput }) =>
      api.post<WorkOrderOffer>(`/work-orders/${workOrderId}/offers`, data),
    onSuccess: (_result, { workOrderId }) => {
      invalidateOfferViews(queryClient, workOrderId);
    },
  });
}

export function useDeliverOffer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      workOrderId,
      offerId,
      data,
    }: {
      workOrderId: string;
      offerId: string;
      data: DeliverOfferBody;
    }) =>
      api.post<DeliverOfferResponse>(
        `/work-orders/${workOrderId}/offers/${offerId}/deliver`,
        data,
      ),
    onSuccess: (_result, { workOrderId }) => {
      invalidateOfferViews(queryClient, workOrderId);
    },
  });
}

export function useRecordOfferDecision() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      workOrderId,
      offerId,
      data,
    }: {
      workOrderId: string;
      offerId: string;
      data: RecordOfferDecisionInput;
    }) =>
      api.post<WorkOrderOffer>(`/work-orders/${workOrderId}/offers/${offerId}/decision`, data),
    onSuccess: (_result, { workOrderId }) => {
      invalidateOfferViews(queryClient, workOrderId);
    },
  });
}

export function useCancelOffer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      workOrderId,
      offerId,
      reason,
    }: {
      workOrderId: string;
      offerId: string;
      reason: string;
    }) =>
      api.post<WorkOrderOffer>(`/work-orders/${workOrderId}/offers/${offerId}/cancel`, { reason }),
    onSuccess: (_result, { workOrderId }) => {
      invalidateOfferViews(queryClient, workOrderId);
    },
  });
}
